import React, { useState, useEffect } from 'react';
import { AlertTriangle, BookOpen, CheckCircle, ChevronRight, Info, Plus, RotateCcw, Search, Tag, Trash2, Upload, X } from 'lucide-react';
import { api } from '../api';
import { TagInput } from '../components/TagInput';

const FormFieldInput = ({ label, name, type = 'text', options, required = false, value, onChange, checked, onCheckChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {type === 'select' ? (
      <select
        className="input-field"
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`${name}-select`}
      >
        <option value="">Select {label}</option>
        {options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    ) : type === 'textarea' ? (
      <textarea
        className="input-field"
        rows={4}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`${name}-textarea`}
      />
    ) : type === 'checkbox' ? (
      <input
        type="checkbox"
        className="w-5 h-5 rounded border-gray-300 text-levelup-accent focus:ring-levelup-accent"
        checked={checked}
        onChange={onCheckChange}
        data-testid={`${name}-checkbox`}
      />
    ) : (
      <input
        type={type}
        className="input-field"
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`${name}-input`}
      />
    )}
  </div>
);

// ContentMetaField — defined OUTSIDE ContentUpload to prevent focus loss on re-render
const ContentMetaField = ({ label, name, type, options, required: req, disabled, placeholder, value, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
      {label}{req && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {type === 'select' ? (
      <select value={value} onChange={e => onChange(name, e.target.value)}
        disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
        data-testid={`${name}-select`}>
        <option value="">Select {label}</option>
        {options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    ) : type === 'textarea' ? (
      <textarea value={value} onChange={e => onChange(name, e.target.value)}
        rows={3} placeholder={placeholder} disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent resize-none ${disabled ? 'bg-gray-50' : ''}`}
        data-testid={`${name}-textarea`} />
    ) : (
      <input type={type || 'text'} value={value} onChange={e => onChange(name, e.target.value)}
        placeholder={placeholder} disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent ${disabled ? 'bg-gray-50 text-gray-500' : ''}`}
        data-testid={`${name}-input`} />
    )}
  </div>
);

const fmtFileSize = (bytes) => {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

// FileUploadItem — defined OUTSIDE ContentUpload; supports up to 10 files per type
const FileUploadItem = ({ fileType, files, uploadProgress, onFileChange, onRemove }) => {
  const typeFiles = files.filter(f => f.type === fileType);
  const canAdd = typeFiles.length < 10;
  return (
    <div className="border-2 border-dashed border-gray-200 rounded-lg p-3 hover:border-levelup-accent transition-colors">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {fileType}
        {typeFiles.length > 0 && (
          <span className="font-normal normal-case text-gray-400 ml-1">({typeFiles.length}/10)</span>
        )}
      </p>
      {typeFiles.length > 0 && (
        <div className="space-y-1 mb-2">
          {typeFiles.map(f => {
            const prog = uploadProgress[f.id];
            const isUploading = prog !== undefined && prog !== null && prog !== 'done';
            const pct = typeof prog === 'number' ? prog : null;
            return (
              <div key={f.id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
                <div className="min-w-0 flex-1 mr-2">
                  <span className="text-xs text-gray-600 block truncate">{f.file.name}</span>
                  <span className="text-xs text-gray-400">{fmtFileSize(f.file.size)}</span>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0">
                  {isUploading && (
                    pct !== null && pct > 0
                      ? <span className="text-xs font-medium text-levelup-accent">{pct}%</span>
                      : <div className="animate-spin w-3 h-3 border-2 border-levelup-accent border-t-transparent rounded-full" />
                  )}
                  {prog === 'done' && <CheckCircle className="text-green-500" size={13} />}
                  {!isUploading && prog !== 'done' && (
                    <button type="button" onClick={() => onRemove(f.id)} className="text-red-400 hover:text-red-600"><X size={13} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {canAdd && (
        <label className="flex flex-col items-center cursor-pointer py-1">
          <Upload className="text-gray-300" size={typeFiles.length > 0 ? 18 : 26} />
          <span className="text-xs text-gray-400 mt-1">{typeFiles.length > 0 ? '+ Add more' : 'Click to upload'}</span>
          <input type="file" className="hidden" multiple
            onChange={e => onFileChange(e, fileType)}
            data-testid={`file-upload-${fileType.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
          />
        </label>
      )}
    </div>
  );
};

const EMPTY_FORM = {
  title: '', description: '', solution_area: '',
  course_type: '', level: '', language: '', target_role: '', target_audience: '',
  duration: '', certification_course: false, hands_on_lab: false,
  catalog_course_id: '', course_code: '', search_tags: []
};

const ContentUpload = ({ user }) => {
  const [metadata, setMetadata] = useState(null);
  const [siabCourses, setSiabCourses] = useState([]);
  const [mode, setMode] = useState('catalog'); // 'catalog' | 'custom'

  // Catalog state
  const [catalogCourses, setCatalogCourses] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogWarning, setCatalogWarning] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [showCatalogDropdown, setShowCatalogDropdown] = useState(false);
  const [selectedCatalogCourse, setSelectedCatalogCourse] = useState(null);
  const [existingSiabCourse, setExistingSiabCourse] = useState(null);
  const [catalogCachedAt, setCatalogCachedAt] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  // Form state (catalog + custom modes)
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [files, setFiles] = useState([]); // [{ id, file, type }]
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // keyed by file id
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        const [meta, coursesData] = await Promise.all([
          api.request('/metadata'),
          api.request('/courses')
        ]);
        setMetadata(meta);
        setSiabCourses(Array.isArray(coursesData) ? coursesData : coursesData.courses || []);
      } catch (err) { console.error(err); }
    };
    init();
  }, []);

  useEffect(() => {
    if (mode === 'catalog') loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const loadCatalog = async () => {
    setCatalogLoading(true);
    try {
      const data = await api.request('/catalog/courses');
      setCatalogCourses(data.courses || []);
      if (data.warning) setCatalogWarning(data.warning);
      if (data.cached_at) setCatalogCachedAt(new Date(data.cached_at).toLocaleDateString());
    } catch (err) {
      setCatalogWarning('Catalog API is unavailable. Use Custom Course mode to upload manually.');
    } finally {
      setCatalogLoading(false);
    }
  };

  const syncCatalogNow = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const data = await api.request('/admin/catalog/refresh', { method: 'POST' });
      setCatalogCourses(data.courses || []);
      if (data.cached_at) setCatalogCachedAt(new Date(data.cached_at).toLocaleDateString());
      setSyncMessage(`Sync complete — ${(data.courses || []).length} courses loaded`);
      setTimeout(() => setSyncMessage(''), 5000);
    } catch (err) {
      setSyncMessage(`Sync failed: ${err.message}`);
      setTimeout(() => setSyncMessage(''), 5000);
    } finally {
      setSyncing(false);
    }
  };

  const filteredCatalog = catalogCourses.filter(c =>
    !catalogSearch || c.title?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
    c.catalog_course_id?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
    c.course_code?.toLowerCase().includes(catalogSearch.toLowerCase())
  );

  const handleSelectCatalogCourse = (course) => {
    setSelectedCatalogCourse(course);
    setCatalogSearch(course.title);
    setShowCatalogDropdown(false);
    setFormData(prev => ({
      ...prev,
      title: course.title || '',
      description: course.description || '',
      solution_area: course.solution_area || '',
      level: course.level || '',
      course_type: course.course_type || '',
      target_audience: course.target_audience || '',
      duration: course.duration || '',
      catalog_course_id: course.catalog_course_id || '',
      course_code: course.course_code || '',
    }));
    const existing = siabCourses.find(s => s.catalog_course_id === course.catalog_course_id);
    setExistingSiabCourse(existing || null);
  };

  const handleField = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  // Multi-file support: append up to 10 per type, each with a unique id
  const handleFileChange = (e, fileType) => {
    const newFiles = Array.from(e.target.files).map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      file,
      type: fileType,
    }));
    setFiles(prev => {
      const existing = prev.filter(f => f.type === fileType);
      const allowed = newFiles.slice(0, 10 - existing.length);
      return [...prev, ...allowed];
    });
    e.target.value = '';
  };

  const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setSubmitError('');
    setSubmitSuccess('');
    try {
      let courseId = existingSiabCourse?.id || null;
      if (!courseId) {
        const result = await api.request('/courses', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        courseId = result.id;
      }
      if (files.length === 0) {
        setSubmitSuccess(existingSiabCourse ? 'Course already exists — no new files uploaded.' : 'Course created. Add files above and upload again.');
        setUploading(false);
        return;
      }
      const fileCount = files.length;
      for (const fileItem of files) {
        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 0 }));
        const fd = new FormData();
        fd.append('file', fileItem.file);
        fd.append('file_type', fileItem.type);
        await api.uploadFile(
          `/courses/${courseId}/files`,
          fd,
          (pct) => setUploadProgress(prev => ({ ...prev, [fileItem.id]: pct }))
        );
        setUploadProgress(prev => ({ ...prev, [fileItem.id]: 'done' }));
      }
      setSubmitSuccess(`Content uploaded successfully! ${fileCount} file(s) added${!existingSiabCourse ? ' and new course created' : ''}.`);
      setFiles([]);
      setUploadProgress({});
      setFormData(EMPTY_FORM);
      setSelectedCatalogCourse(null);
      setCatalogSearch('');
      setExistingSiabCourse(null);
      const freshCourses = await api.request('/courses');
      setSiabCourses(freshCourses.map(c => ({ ...c, files: c.files || [] })));
    } catch (err) {
      setSubmitError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setFormData(EMPTY_FORM);
    setSelectedCatalogCourse(null);
    setCatalogSearch('');
    setExistingSiabCourse(null);
    setFiles([]);
    setUploadProgress({});
    setSubmitSuccess('');
    setSubmitError('');
  };

  return (
    <div className="space-y-4 max-w-5xl">
      {/* Header */}
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <h1 className="text-xl font-bold">Upload Course Content</h1>
        <p className="text-gray-300 mt-1">Add content for a catalog course or create a custom course</p>
      </div>

      {/* Mode Tabs — outer div must NOT have overflow-hidden so the catalog search dropdown can overflow the card boundary */}
      <div className="bg-white rounded-xl shadow-md">
        <div className="flex border-b border-gray-100 overflow-hidden rounded-t-xl">
          <button onClick={() => switchMode('catalog')}
            className={`flex-1 py-4 text-sm font-semibold transition-all flex items-center justify-center space-x-2 ${mode === 'catalog' ? 'bg-levelup-dark text-white' : 'text-gray-500 hover:text-levelup-dark hover:bg-gray-50'}`}
            data-testid="mode-catalog">
            <BookOpen size={16} /><span>Catalog Course</span>
          </button>
          <button onClick={() => switchMode('custom')}
            className={`flex-1 py-4 text-sm font-semibold transition-all flex items-center justify-center space-x-2 ${mode === 'custom' ? 'bg-levelup-dark text-white' : 'text-gray-500 hover:text-levelup-dark hover:bg-gray-50'}`}
            data-testid="mode-custom">
            <Plus size={16} /><span>Custom Course</span>
          </button>
        </div>

        {/* ── CATALOG + CUSTOM FORM ────────────────────────────────── */}
        {(
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* CATALOG MODE */}
            {mode === 'catalog' && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-levelup-dark">Search PULSE Catalog</h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {catalogLoading ? 'Loading catalog…' : catalogCourses.length > 0
                        ? `${catalogCourses.length} courses available${catalogCachedAt ? ` · Last synced: ${catalogCachedAt}` : ''}`
                        : catalogWarning ? 'Catalog unavailable' : 'No courses in catalog'}
                    </p>
                  </div>
                <div className="flex flex-col items-end gap-1">
                  <button type="button" onClick={syncCatalogNow}
                    disabled={syncing || catalogLoading}
                    className="flex items-center space-x-1.5 px-3 py-1.5 bg-levelup-accent text-levelup-darker text-xs font-semibold rounded-lg hover:bg-levelup-accent-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="sync-catalog-btn">
                    <RotateCcw size={13} className={syncing ? 'animate-spin' : ''} />
                    <span>{syncing ? 'Syncing…' : 'Sync Catalog Now'}</span>
                  </button>
                  {syncMessage && (
                    <p className={`text-xs ${syncMessage.startsWith('Sync failed') ? 'text-red-500' : 'text-green-600'}`}>
                      {syncMessage}
                    </p>
                  )}
                </div>
                </div>

                {/* Catalog Search — always visible */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input type="text" value={catalogSearch}
                      onChange={e => { setCatalogSearch(e.target.value); setShowCatalogDropdown(true); setSelectedCatalogCourse(null); }}
                      onFocus={() => setShowCatalogDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCatalogDropdown(false), 150)}
                      placeholder={catalogLoading ? 'Loading catalog…' : catalogCourses.length > 0 ? 'Search by course name or ID…' : 'Catalog unavailable — switch to Custom Course'}
                      disabled={catalogLoading || catalogCourses.length === 0}
                      className={`w-full pl-10 pr-10 py-3 border-2 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent text-sm transition-colors ${catalogCourses.length === 0 && !catalogLoading ? 'border-yellow-200 bg-yellow-50 text-yellow-700 cursor-not-allowed' : 'border-gray-200'}`}
                      data-testid="catalog-search-input"
                    />
                    {catalogLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin w-4 h-4 border-2 border-levelup-accent border-t-transparent rounded-full" />}
                    {selectedCatalogCourse && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" size={18} />}
                  </div>

                  {/* Dropdown results */}
                  {showCatalogDropdown && catalogSearch && (
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-y-auto">
                      {filteredCatalog.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">No courses match "{catalogSearch}"</div>
                      ) : (
                        filteredCatalog.slice(0, 20).map(c => (
                          <button key={c.catalog_course_id} type="button"
                            onMouseDown={() => handleSelectCatalogCourse(c)}
                            className="w-full text-left px-3 py-2 hover:bg-levelup-accent/10 transition-colors border-b border-gray-50 last:border-0"
                            data-testid={`catalog-option-${c.catalog_course_id}`}>
                            <p className="text-sm font-medium text-levelup-dark">{c.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {[c.course_code, c.solution_area, c.level, c.course_type].filter(Boolean).join(' · ')}
                            </p>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {catalogWarning && catalogCourses.length === 0 && (
                  <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm text-yellow-800">{catalogWarning}</p>
                      <button type="button" onClick={() => switchMode('custom')}
                        className="text-xs text-yellow-700 underline mt-1">Switch to Custom Course</button>
                    </div>
                  </div>
                )}

                {existingSiabCourse && (
                  <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="existing-course-notice">
                    <Info className="text-blue-500 flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-sm text-blue-800">
                      This catalog course already exists in SIAB as "<strong>{existingSiabCourse.title}</strong>".
                      New files will be added to the existing course.
                    </p>
                  </div>
                )}

                {selectedCatalogCourse && (
                  <div className="bg-gray-50 rounded-xl p-5 space-y-4 border border-gray-100">
                    <div className="flex items-center space-x-2 mb-1">
                      <CheckCircle className="text-green-500" size={16} />
                      <span className="text-sm font-semibold text-gray-700">Course metadata auto-populated from catalog</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { label: 'Course Title', value: formData.title },
                        { label: 'Course ID', value: formData.catalog_course_id },
                        { label: 'Course Type', value: formData.course_type },
                        { label: 'Solution Area', value: formData.solution_area },
                        { label: 'Level', value: formData.level },
                        { label: 'Target Audience', value: formData.target_audience },
                        { label: 'Duration', value: formData.duration },
                      ].filter(f => f.value).map(f => (
                        <div key={f.label} className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-xs text-gray-400 font-medium">{f.label}</p>
                          <p className="text-sm text-gray-800 mt-0.5 font-medium truncate">{f.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-levelup-accent/30">
                      <label className="block text-xs font-semibold text-levelup-accent uppercase tracking-wide mb-1.5">
                        Language <span className="text-red-500">*</span>
                        <span className="text-gray-400 font-normal normal-case ml-1">(not in catalog — please select)</span>
                      </label>
                      <select value={formData.language} onChange={e => handleField('language', e.target.value)}
                        required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent"
                        data-testid="catalog-language-select">
                        <option value="">Select Language</option>
                        {metadata?.languages?.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-wrap gap-4 pt-1">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={formData.certification_course}
                          onChange={e => handleField('certification_course', e.target.checked)}
                          className="w-4 h-4 rounded text-levelup-accent focus:ring-levelup-accent"
                          data-testid="cert-course-checkbox" />
                        <span className="text-sm text-gray-700">Certification Course</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={formData.hands_on_lab}
                          onChange={e => handleField('hands_on_lab', e.target.checked)}
                          className="w-4 h-4 rounded text-levelup-accent focus:ring-levelup-accent"
                          data-testid="hol-checkbox" />
                        <span className="text-sm text-gray-700">Includes Hands-on Lab</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CUSTOM MODE */}
            {mode === 'custom' && (
              <div className="space-y-5">
                <p className="text-sm text-gray-500">Fill in course details. Fields match the PULSE catalog metadata schema.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ContentMetaField label="Course Title" name="title" required placeholder="e.g. M365 Copilot Fundamentals"
                    value={formData.title} onChange={handleField} />
                  <ContentMetaField label="Course ID / Code" name="course_code" placeholder="e.g. TP-001 (optional)"
                    value={formData.course_code} onChange={handleField} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <ContentMetaField label="Course Type" name="course_type" type="select" options={metadata?.course_types} required
                    value={formData.course_type} onChange={handleField} />
                  <ContentMetaField label="Solution Area" name="solution_area" type="select" options={metadata?.solution_areas} required
                    value={formData.solution_area} onChange={handleField} />
                  <ContentMetaField label="Level" name="level" type="select" options={metadata?.levels} required
                    value={formData.level} onChange={handleField} />
                  <ContentMetaField label="Target Audience" name="target_audience" type="select" options={metadata?.target_audiences} required
                    value={formData.target_audience} onChange={handleField} />
                  <ContentMetaField label="Language" name="language" type="select" options={metadata?.languages} required
                    value={formData.language} onChange={handleField} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ContentMetaField label="Duration" name="duration" placeholder="e.g. 4 hours, 2 days"
                    value={formData.duration} onChange={handleField} />
                </div>
                <ContentMetaField label="Description" name="description" type="textarea" required
                  placeholder="Briefly describe course objectives and outcomes"
                  value={formData.description} onChange={handleField} />
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" checked={formData.certification_course}
                      onChange={e => handleField('certification_course', e.target.checked)}
                      className="w-4 h-4 rounded text-levelup-accent focus:ring-levelup-accent"
                      data-testid="cert-course-checkbox" />
                    <span className="text-sm text-gray-700">Certification Course</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" checked={formData.hands_on_lab}
                      onChange={e => handleField('hands_on_lab', e.target.checked)}
                      className="w-4 h-4 rounded text-levelup-accent focus:ring-levelup-accent"
                      data-testid="hol-checkbox" />
                    <span className="text-sm text-gray-700">Includes Hands-on Lab</span>
                  </label>
                </div>
              </div>
            )}

            {/* FILE UPLOADS — shared by catalog + custom */}
            {(mode === 'custom' || selectedCatalogCourse) && (
              <div>
                {/* Search Tags */}
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <Tag size={12} /> Search Tags <span className="font-normal normal-case text-gray-400 ml-1">(optional — improves discoverability)</span>
                  </label>
                  <TagInput
                    value={formData.search_tags}
                    onChange={(tags) => handleField('search_tags', tags)}
                  />
                </div>
                <h3 className="text-sm font-semibold text-levelup-dark uppercase tracking-wide mb-1">Course Content Files</h3>
                <p className="text-xs text-gray-400 mb-3">Up to 10 files per type (e.g. one per module)</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {metadata?.file_types?.map(ft => (
                    <FileUploadItem key={ft} fileType={ft} files={files}
                      uploadProgress={uploadProgress}
                      onFileChange={handleFileChange} onRemove={removeFile} />
                  ))}
                </div>
              </div>
            )}

            {submitSuccess && (
              <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="upload-success">
                <CheckCircle className="text-green-500 flex-shrink-0" size={18} />
                <p className="text-sm text-green-800">{submitSuccess}</p>
              </div>
            )}
            {submitError && (
              <div className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="upload-error">
                <AlertTriangle className="text-red-500 flex-shrink-0" size={18} />
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            )}

            {(mode === 'custom' || selectedCatalogCourse) && (
              <button type="submit" disabled={uploading ||
                (mode === 'catalog' && !formData.language) ||
                (mode === 'custom' && (!formData.title || !formData.solution_area || !formData.course_type || !formData.level || !formData.language))}
                className="w-full bg-levelup-accent text-levelup-darker py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
                data-testid="submit-upload">
                {uploading
                  ? (() => {
                      const vals = Object.values(uploadProgress).filter(v => typeof v === 'number');
                      const pct = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / files.length) : 0;
                      return <><div className="animate-spin w-5 h-5 border-2 border-levelup-darker border-t-transparent rounded-full" /><span>Uploading… {pct > 0 ? `${pct}%` : ''}</span></>;
                    })()
                  : <><Upload size={18} /><span>{existingSiabCourse ? 'Add Files to Existing Course' : mode === 'catalog' ? 'Create Course & Upload Files' : 'Create Custom Course & Upload'}</span></>}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

// Chatbot Insights Component (Admin only)

export default ContentUpload;
